import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getInitials } from "@/hooks/getInitials";
import { Loader2, MapPin } from "lucide-react";
import React from "react";


const MyProfile = ({ profile, setProfile, fetchCurrentLocation,fetchingLocation,handleUpdateMyProfile }) => {
  return (
     <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Information Card */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-2xl font-bold text-[#1A3C34] mb-6">Profile Information</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Full Name</Label>
                  <Input 
                    type="text" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3C34]"
                    placeholder="John Doe"
                    value={profile.fullName}
                    onChange={(e) => setProfile({ ...profile, fullName: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Email</Label>
                  <Input 
                    type="email" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3C34]"
                    placeholder="john.doe@example.com"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Phone Number</Label>
                  <Input 
                    type="tel" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3C34]"
                    placeholder="+1 (555) 000-0000"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Organization</Label>
                  <Input 
                    type="text" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3C34]"
                    placeholder="Your Organization"
                    value={profile.organization}
                    onChange={(e) => setProfile({ ...profile, organization: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">Address</Label>
                <div className="flex gap-2">
                  <Input 
                    type="text" 
                    value={profile.address}
                    onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3C34]"
                    placeholder="123 Main Street"
                  />
                  <Button
                    type="button"
                    onClick={fetchCurrentLocation}
                    disabled={fetchingLocation}
                    className="bg-[#1A3C34] hover:bg-[#2A4C44] text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    {fetchingLocation ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <MapPin size={16} />
                    )}
                    {fetchingLocation ? "Getting..." : "Use Current"}
                  </Button>
                </div>
                {location.latitude && location.longitude && (
                  <p className="text-xs text-gray-500 mt-1">
                    Coordinates: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">City</Label>
                  <Input 
                    type="text" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3C34]"
                    placeholder="San Francisco"
                    value={profile.city}
                    onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">Zip Code</Label>
                  <Input 
                    type="text" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3C34]"
                    placeholder="94102"
                    value={profile.zipCode}
                    onChange={(e) => setProfile({ ...profile, zipCode: e.target.value })}
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button className="w-full md:w-auto bg-[#1A3C34] hover:bg-[#2A4C44] text-white px-8 py-2 rounded-lg" onClick={handleUpdateMyProfile}>
                  Save Changes
                </Button>
              </div>
            </div>
          </div>

          {/* Profile Summary Card */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#1A3C34] to-[#2A4C44] rounded-2xl shadow-sm p-6 text-white">
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <span className="text-4xl font-bold">{getInitials(profile.fullName)}</span>
                </div>
                <h3 className="text-xl font-bold mb-1">{profile.fullName}</h3>
                <p className="text-white/80 text-sm mb-4">{profile.email}</p>
                <Button variant="outline" className="border-white text-white hover:bg-white/10">
                  Change Photo
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-[#1A3C34] mb-4">Account Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Bookings</span>
                  <span className="font-bold text-[#1A3C34]">24</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Active Vehicles</span>
                  <span className="font-bold text-[#1A3C34]">2</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Carpool Matches</span>
                  <span className="font-bold text-[#1A3C34]">8</span>
                </div>
              </div>
            </div>
          </div>
        </div>
  );
};

export default MyProfile;